import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { matchPath } from 'react-router';
import * as api from './admicomAPI';
import KPConfig from '../../KPConfig';
import { objectIdPaths } from '../../app/paths';

const initialState = {
  allApplications: [],
  userApplications: [],
  tenant: {}, // Current tenant, linked to user current company.admicom_tenant_id
  tenants: [],
  applicationsStatus: 'idle',
  tenantStatus: 'idle',
  tenantChangeStatus: 'idle',
};

export const getApplications = createAsyncThunk(
  'admicom/getApplications',
  async () => {
    const response = await api.getApplications();
    return response.body;
  }
);

export const getTenants = createAsyncThunk(
  'admicom/getTenants',
  async () => {
    const response = await api.getTenants();
    return response.body;
  }
);

export const changeTenant = createAsyncThunk(
  'admicom/changeTenant',
  async (tenantId) => {
    const response = await api.changeTenant(tenantId);
    // Reload so the rest of the app picks up the new tenant context.
    // Kept out of the reducer to preserve reducer purity.
    //
    // Pages that show a specific object (or a specific folder) belong to the
    // previous tenant and won't resolve / the user has no rights to them after
    // the switch, so we send the user to the root folder view. Generic pages
    // (front page, folders root, account, admin, subscription, ...) are
    // tenant-agnostic, so we just reload them in place. `objectIdPaths` is the
    // canonical list of object-scoped routes (reused from src/app/paths.js so
    // this stays in sync as routes change).
    const { pathname } = window.location;
    const isObjectPage = objectIdPaths.some((path) => matchPath({ path }, pathname));

    if (isObjectPage) {
      window.location.href = KPConfig.publicUrl + '/folders';
    } else {
      window.location.reload();
    }
    return response.body;
  }
);

export const admicomSlice = createSlice({
  name: 'admicom',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      // Get applications
      .addCase(getApplications.pending, (state) => {
        state.applicationsStatus = 'loading';
      })
      .addCase(getApplications.fulfilled, (state, action) => {
        state.applicationsStatus = 'succeeded';
        state.allApplications = action.payload.allApplications || [];
        state.userApplications = action.payload.userApplications || [];
      })
      .addCase(getApplications.rejected, (state) => {
        state.applicationsStatus = 'failed';
      })
      .addCase(getTenants.pending, (state) => {
        state.tenantStatus = 'loading';
      })
      .addCase(getTenants.fulfilled, (state, action) => {
        state.tenantStatus = 'succeeded';
        state.tenant = action.payload.tenant || {};
        state.tenants = action.payload.tenants || [];
      })
      .addCase(getTenants.rejected, (state) => {
        state.tenantStatus = 'failed';
      })
      .addCase(changeTenant.pending, (state) => {
        state.tenantChangeStatus = 'loading';
      })
      .addCase(changeTenant.fulfilled, (state) => {
        state.tenantChangeStatus = 'succeeded';
      })
      .addCase(changeTenant.rejected, (state) => {
        state.tenantChangeStatus = 'failed';
      });
  },
});

// Selectors
export const selectAllApplications = (state) => state.admicom.allApplications;
export const selectUserApplications = (state) => state.admicom.userApplications;
export const selectTenant = (state) => state.admicom.tenant;
export const selectTenants = (state) => state.admicom.tenants;
export const selectTenantStatus = (state) => state.admicom.tenantStatus;
export const selectTenantChangeStatus = (state) => state.admicom.tenantChangeStatus;

export const selectApplicationsStatus = (state) => state.admicom.applicationsStatus;

export default admicomSlice.reducer;
